"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { MenuItem } from "@/lib/generated/graphql"
import MobileNavigationHighlighter from "./MobileNavigationHighlighter"
import GoogleSearch from "./GoogleSearch"

interface MobileMenuProps {
  menuItems: MenuItem[]
}

export default function MobileMenu({ menuItems }: MobileMenuProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null)

  const toggleSubmenu = (path: string) => {
    setOpenSubmenu(openSubmenu === path ? null : path)
  }

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }

    return () => {
      document.body.style.overflow = ""
    }
  }, [isOpen])

  return (
    <div className="md:hidden">
      {/* Hamburger button - only show when menu is closed */}
      {!isOpen && (
        <button
          className="flex h-10 w-10 items-center justify-center"
          onClick={() => setIsOpen(true)}
          aria-label="Open menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M4 6H20M4 12H20M4 18H20"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {/* Mobile menu overlay */}
      <div
        className={`bg-interest-blue fixed left-0 top-0 z-20 h-full w-[70%] transition-transform duration-300 ${isOpen ? "translate-x-0 shadow-[4px_0_10px_rgba(0,0,0,0.15)]" : "-translate-x-full"}`}
      >
        {/* Search and Close button at the top */}
        <div className="flex items-center justify-between p-4">
          <div className="flex-1 pr-4">
            <GoogleSearch />
          </div>
          <button
            className="flex h-10 w-10 items-center justify-center text-white"
            onClick={() => setIsOpen(false)}
            aria-label="Close menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6 18L18 6M6 6L18 18"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <nav className="flex flex-col overflow-y-auto px-5 pb-8">
          {menuItems.map((item) => (
            <div key={item.url?.path} className="py-2">
              {item.children && item.children.length > 0 ? (
                <div>
                  <button
                    onClick={() => toggleSubmenu(item.url?.path || "")}
                    className="flex w-full items-center justify-between text-sm font-bold tracking-[0.3px] text-white"
                    data-menu-path={item.url?.path || ""}
                    data-has-children="true"
                  >
                    {item.title}
                    <span>{openSubmenu === item.url?.path ? "▲" : "▼"}</span>
                  </button>
                  {openSubmenu === item.url?.path && (
                    <div className="ml-4 mt-2 flex flex-col space-y-2">
                      {item.children.map((childItem) => (
                        <Link
                          key={childItem.url?.path}
                          href={childItem.url?.path || "#"}
                          className="block py-1 text-sm text-white"
                          onClick={() => setIsOpen(false)}
                          prefetch={false}
                        >
                          {childItem.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  href={item.url?.path || "#"}
                  className="block text-sm font-bold tracking-[0.3px] text-white"
                  onClick={() => setIsOpen(false)}
                >
                  {item.title}
                </Link>
              )}
            </div>
          ))}
        </nav>
        <MobileNavigationHighlighter />
      </div>
    </div>
  )
}
